import { useState } from "react";
import { Link2, Loader2, Mail, Phone } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@bookdental/ui";
import { trpc } from "@/lib/trpc";
import { AJUDA_PACIENTES } from "@/lib/ajuda/dialogos/pacientes";
import { env } from "@/lib/env";
import { ResultStage, DialogField, formatExpiry } from "@/components/shared/InviteResultStage";

type Props = {
  open: boolean;
  onClose: () => void;
};

type Stage =
  | { kind: "form" }
  | {
      kind: "result";
      token: string;
      email: string | null;
      phone: string | null;
      expiresAt: Date | null;
    };

const EXPIRY_OPTIONS = [
  { days: 1, label: "24 horas" },
  { days: 3, label: "3 dias" },
  { days: 7, label: "7 dias" },
  { days: 30, label: "30 dias" },
];

function formatPhone(v: string): string {
  const d = v.replace(/\D/g, "").slice(0, 11);
  if (d.length <= 2) return d ? `(${d}` : "";
  if (d.length <= 6) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
  if (d.length <= 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
}

/** Retorna número pronto pra wa.me (55 + DDD + número, só dígitos). */
function toWaNumber(phone: string): string {
  const d = phone.replace(/\D/g, "");
  if (!d) return "";
  return d.startsWith("55") ? d : `55${d}`;
}

export function PatientInviteLinkDialog({ open, onClose }: Props) {
  const [stage, setStage] = useState<Stage>({ kind: "form" });
  const [expiresInDays, setExpiresInDays] = useState(7);
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [copied, setCopied] = useState(false);
  const [showQr, setShowQr] = useState(false);

  const utils = trpc.useUtils();
  const create = trpc.patient.invite.create.useMutation({
    onSuccess: (invite) => {
      setStage({
        kind: "result",
        token: invite.token,
        email: invite.email,
        phone: invite.phone,
        expiresAt: invite.expiresAt ? new Date(invite.expiresAt) : null,
      });
      utils.patient.invite.list.invalidate();
    },
    onError: (err) => toast.error(err.message || "Erro ao gerar link."),
  });

  const handleClose = () => {
    onClose();
    setTimeout(() => {
      setStage({ kind: "form" });
      setExpiresInDays(7);
      setEmail("");
      setPhone("");
      setCopied(false);
      setShowQr(false);
    }, 250);
  };

  const inviteUrl =
    stage.kind === "result" ? `${env.landingUrl}/p/${stage.token}` : "";

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(inviteUrl);
      setCopied(true);
      toast.success("Link copiado.");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Não foi possível copiar.");
    }
  };

  const whatsappText = `Olá! Preencha seu cadastro aqui: ${inviteUrl}`;
  const whatsappHref =
    stage.kind === "result"
      ? stage.phone
        ? `https://wa.me/${toWaNumber(stage.phone)}?text=${encodeURIComponent(whatsappText)}`
        : `https://wa.me/?text=${encodeURIComponent(whatsappText)}`
      : "";

  const emailSubject = "Seu cadastro de paciente";
  const emailBody = `Olá!%0D%0A%0D%0APara completar seu cadastro, acesse o link abaixo:%0D%0A${encodeURIComponent(
    inviteUrl,
  )}%0D%0A%0D%0ANos vemos em breve.`;
  const mailtoHref =
    stage.kind === "result" && stage.email
      ? `mailto:${stage.email}?subject=${encodeURIComponent(emailSubject)}&body=${emailBody}`
      : "";

  const expiryLabel =
    stage.kind === "result" && stage.expiresAt
      ? formatExpiry(stage.expiresAt)
      : null;

  const handleCreate = () => {
    create.mutate({
      expiresInDays,
      email: email.trim() || undefined,
      phone: phone.replace(/\D/g, "") || undefined,
    });
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent className="max-w-md dark:border-border dark:bg-card">
        <DialogHeader>
          <DialogTitle
            className="dark:text-foreground"
            ajuda={stage.kind === "form" ? AJUDA_PACIENTES.linkDeCadastro : AJUDA_PACIENTES.linkGerado}
          >
            {stage.kind === "form" ? "Gerar link de cadastro" : "Link gerado"}
          </DialogTitle>
          <DialogDescription className="dark:text-muted-foreground">
            {stage.kind === "form"
              ? "Paciente preenche o cadastro pelo link. Se informar contato, ele é pré-preenchido e você envia direto."
              : "Compartilhe o link com o paciente."}
          </DialogDescription>
        </DialogHeader>

        {stage.kind === "form" && (
          <div className="space-y-4">
            <div>
              <label className="mb-2 block text-[11px] font-semibold uppercase tracking-wider text-zinc-500 dark:text-muted-foreground">
                Validade do link
              </label>
              <div className="grid grid-cols-4 gap-1.5">
                {EXPIRY_OPTIONS.map((opt) => {
                  const active = expiresInDays === opt.days;
                  return (
                    <button
                      key={opt.days}
                      type="button"
                      onClick={() => setExpiresInDays(opt.days)}
                      className={
                        active
                          ? "rounded-lg border border-brand-500 bg-brand-50 px-2 py-2 text-[11px] font-semibold text-brand-900 dark:border-primary dark:bg-primary/10 dark:text-primary"
                          : "rounded-lg border border-zinc-200 bg-white px-2 py-2 text-[11px] font-medium text-zinc-600 transition-colors hover:border-zinc-300 hover:text-zinc-900 dark:border-border dark:bg-background dark:text-foreground/80 dark:hover:border-border"
                      }
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <label className="mb-2 block text-[11px] font-semibold uppercase tracking-wider text-zinc-500 dark:text-muted-foreground">
                Contato do paciente (opcional)
              </label>
              <p className="mb-2 text-[11px] leading-relaxed text-zinc-500 dark:text-muted-foreground">
                Se preencher, o formulário abre pré-preenchido e você envia
                direto pelo canal informado.
              </p>

              <div className="space-y-2">
                <DialogField icon={Phone} label="Celular / WhatsApp">
                  <input
                    type="tel"
                    name="bd-invite-phone"
                    autoComplete="off"
                    data-1p-ignore
                    data-lpignore
                    value={formatPhone(phone)}
                    onChange={(e) =>
                      setPhone(e.target.value.replace(/\D/g, "").slice(0, 11))
                    }
                    placeholder="(11) 98765-4321"
                    inputMode="tel"
                    className="w-full bg-transparent text-sm text-zinc-900 outline-none placeholder:text-zinc-400 dark:text-foreground dark:placeholder:text-muted-foreground"
                  />
                </DialogField>

                <DialogField icon={Mail} label="E-mail">
                  <input
                    type="email"
                    name="bd-invite-email"
                    autoComplete="off"
                    data-1p-ignore
                    data-lpignore
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="paciente@exemplo.com"
                    className="w-full bg-transparent text-sm text-zinc-900 outline-none placeholder:text-zinc-400 dark:text-foreground dark:placeholder:text-muted-foreground"
                  />
                </DialogField>
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={handleClose}
                className="inline-flex h-9 items-center rounded-md border border-zinc-200 px-3 text-[13px] font-medium text-zinc-700 transition-colors hover:bg-zinc-50 dark:border-border dark:text-foreground dark:hover:bg-muted"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleCreate}
                disabled={create.isPending}
                className="inline-flex h-9 items-center gap-1.5 rounded-md bg-brand-600 px-4 text-[13px] font-semibold text-white shadow-sm transition-colors hover:bg-brand-700 disabled:opacity-60"
              >
                {create.isPending ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Link2 className="h-3.5 w-3.5" />
                )}
                Gerar link
              </button>
            </div>
          </div>
        )}

        {stage.kind === "result" && (
          <ResultStage
            inviteUrl={inviteUrl}
            phone={stage.phone}
            email={stage.email}
            whatsappHref={whatsappHref}
            mailtoHref={mailtoHref}
            copied={copied}
            onCopy={copy}
            showQr={showQr}
            onToggleQr={() => setShowQr((v) => !v)}
            expiryLabel={expiryLabel}
            onNewLink={() => {
              setStage({ kind: "form" });
              setEmail("");
              setPhone("");
              setShowQr(false);
            }}
            onClose={handleClose}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

// ─── Result stage, reutilizado em outros dialogs ──────────


